import { Helmet } from 'react-helmet-async';
import seo from '@/seo/pages.json';
import seoLanguages from '@/seo/languages.json';
import { landingTitle, landingDescription } from '@/seo/landingMeta.mjs';
import { useTranslation } from '@/contexts/I18nContext';

const { site, pages } = seo;
const { languages } = seoLanguages;

const absUrl = (path) => (path === '/' ? `${site.url}/` : `${site.url}${path}`);
const langByCode = Object.fromEntries(languages.map((l) => [l.code, l]));

function alternatesFor(page, isLanding) {
  if (isLanding || page.alternates) {
    return languages.map((l) => ({ hreflang: l.hreflang, href: absUrl(l.path) }))
      .concat([{ hreflang: 'x-default', href: absUrl('/') }]);
  }
  if (!page.group) return null;
  const members = Object.entries(pages).filter(([, p]) => p.group === page.group);
  const fallback = members.find(([, p]) => (p.lang || 'en') === 'en') || members[0];
  return members.map(([route, p]) => ({ hreflang: langByCode[p.lang || 'en'].hreflang, href: absUrl(route) }))
    .concat([{ hreflang: 'x-default', href: absUrl(fallback[0]) }]);
}

// Helmet replaces every build-time head tag on mount, so this must emit the
// same tag set as scripts/prerender-seo.js or the missing ones disappear.
export default function SEO({ path, landing, title, description, noindex = false }) {
  const { t } = useTranslation();
  const lang = landing ? languages.find((l) => l.code === landing) : null;
  const page = (path && pages[path]) || {};

  let fullTitle = title ? `${title} | ${site.name}` : page.title || site.defaultTitle;
  let desc = description || page.description || site.defaultDescription;
  let canonicalPath = path;
  if (lang) {
    fullTitle = landingTitle(lang, t);
    desc = landingDescription(lang, t);
    canonicalPath = lang.path;
  }
  const url = canonicalPath && !noindex ? absUrl(canonicalPath) : null;
  const alternates = noindex ? null : alternatesFor(page, !!lang);
  const locale = (lang || langByCode[page.lang || 'en']).locale;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {noindex && <meta name="robots" content="noindex" />}
      {url && <link rel="canonical" href={url} />}
      {alternates && alternates.map((a) => (
        <link key={a.hreflang} rel="alternate" hrefLang={a.hreflang} href={a.href} />
      ))}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={site.name} />
      <meta property="og:locale" content={locale} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:image" content={site.image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={site.imageAlt} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={site.image} />
      {url && <meta property="og:url" content={url} />}
    </Helmet>
  );
}
